import Swal from "sweetalert2";

/**
 * Validates a file based on size and type.
 * @param {File} file - The file to validate.
 * @param {Object} options - Validation options.
 * @param {number} options.maxSizeMB - Maximum file size in MB.
 * @param {Array<string>} options.allowedTypes - Allowed MIME types.
 * @returns {Object} - { valid: boolean }
 */
export const validateFile = (file, { maxSizeMB = 5, allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'] } = {}) => {
  if (!file) return { valid: false };

  // Global rule: no video
  if (file.type.startsWith('video/')) {
    Swal.fire({
      icon: 'error',
      title: 'Invalid File Type',
      text: 'Video uploads are not allowed on any page.',
    });
    return { valid: false };
  }

  // Check allowed types
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    Swal.fire({
      icon: 'error',
      title: 'Invalid File Type',
      text: `Allowed file types are: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`,
    });
    return { valid: false };
  }

  // Check size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    Swal.fire({
      icon: 'error',
      title: 'File Too Large',
      text: `Maximum allowed file size is ${maxSizeMB}MB.`,
    });
    return { valid: false };
  }

  return { valid: true };
};

/**
 * Resizes an image file to specified dimensions.
 * @param {File} file - The image file to resize.
 * @param {number} targetWidth - Target width.
 * @param {number} targetHeight - Target height.
 * @returns {Promise<File>} - A promise that resolves to the resized File object.
 */
export const resizeImage = (file, targetWidth = 512, targetHeight = 512) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file); // Return original if not an image
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        // Draw image resized (crop/fit logic could be added here, but simple stretch/shrink for now or centered fit)
        // Simple stretch to fill the 512x512 box as per "force resize like 512x512"
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas to Blob conversion failed'));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        }, file.type, 0.9); // 0.9 quality
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};
